import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Upload, Loader2, ShieldCheck, Lock, X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@meno/ui/components/ui/card'
import { Skeleton } from '@meno/ui/components/ui/skeleton'
import {
  BACKEND_URL, getLicenseFallbackImage, updateLicenseFallbackImage, uploadMedia,
} from '@meno/ui/lib/api'
import { cn } from '@meno/ui/lib/utils'
import { useAuth } from '@meno/ui/contexts/AuthContext'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import AppNotificationsCard from '@/components/AppNotificationsCard'

/**
 * Frame settings — what applies to every screen this organization runs.
 */
export default function SettingsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  usePageHeader({
    title: t('nav.frameSettings', 'Settings'),
    subtitle: t('frameSettings.subtitle', 'Settings for all of your screens.'),
  })
  return (
    <div className="w-full max-w-3xl space-y-6 pb-16">
      <AppNotificationsCard />
      <LicenseFallbackCard isOwner={user?.role === 'owner'} />
    </div>
  )
}

// License fallback image (Enterprise-only): shown full-bleed on every screen
// this org owns instead of the standard unpair -> pairing-code flow when a
// screen's license expires. See backend/app/services/license_fallback.py.
// Lives in Frame's own Settings: it only affects screens, so it belongs with
// the app that runs them (moved back from the My app's Organization page,
// 2026-10-10, at the user's request).
function LicenseFallbackCard({ isOwner }: { isOwner: boolean }) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)

  const [url, setUrl] = useState<string | null>(null)
  const [entitled, setEntitled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [imgFailed, setImgFailed] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const r = await getLicenseFallbackImage()
        setUrl(r.data.url)
        setEntitled(r.data.entitled)
      } catch {
        // fall through to the empty/locked state
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  useEffect(() => { setImgFailed(false) }, [url])

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (inputRef.current) inputRef.current.value = ''
    if (!file) return
    setUploading(true)
    try {
      const { data: uploaded } = await uploadMedia(file, undefined, 'signage')
      await updateLicenseFallbackImage(uploaded.url)
      setUrl(uploaded.url)
      toast.success(t('license.uploadSuccess', 'Fallback image saved'))
    } catch {
      toast.error(t('license.uploadError', 'Upload failed'))
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = async () => {
    try {
      await updateLicenseFallbackImage(null)
      setUrl(null)
    } catch {
      toast.error(t('license.removeError', 'Failed to remove'))
    }
  }

  const resolvedUrl = !url ? null : imgFailed ? null : (url.startsWith('http') ? url : `${BACKEND_URL}${url}`)

  return (
    <Card className="bg-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-muted-foreground" />
          {t('license.title', 'License fallback image')}
        </CardTitle>
        <p className="text-muted-foreground text-sm mt-1">
          {t(
            'license.subtitle',
            'If a screen’s license expires, it normally drops to a pairing code until you re-license and re-pair it by hand. On the Enterprise plan, your screens stay paired instead and show this image until the license is renewed — no re-pairing needed.',
          )}
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-56 w-full rounded-2xl" />
        ) : !entitled ? (
          <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-8 flex flex-col items-center text-center gap-3">
            <div className="p-3 rounded-xl bg-muted">
              <Lock className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="font-semibold text-sm">{t('license.lockedTitle', 'Enterprise plan only')}</p>
            <p className="text-xs text-muted-foreground max-w-sm">
              {t('license.lockedBody', 'Upgrade to Enterprise to keep screens paired and showing a branded image instead of a pairing code when a license lapses.')}
            </p>
          </div>
        ) : (
          <>
            <div
              className={cn(
                'relative group rounded-2xl border-2 border-dashed border-border bg-background overflow-hidden transition-all h-56',
                isOwner && 'hover:border-primary/60 hover:shadow-md cursor-pointer',
              )}
              onClick={() => isOwner && !uploading && inputRef.current?.click()}
            >
              {resolvedUrl && (
                <img
                  src={resolvedUrl}
                  alt=""
                  className="absolute inset-0 w-full h-full object-contain p-4"
                  onError={() => setImgFailed(true)}
                />
              )}
              <div
                className={cn(
                  'absolute inset-0 flex flex-col items-center justify-center gap-2 transition-opacity p-4',
                  resolvedUrl ? 'opacity-0 group-hover:opacity-100 bg-black/50' : 'opacity-100',
                )}
              >
                {uploading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                ) : (
                  <div className={cn('p-3 rounded-xl', resolvedUrl ? 'bg-white/20' : 'bg-muted')}>
                    <Upload className={cn('w-5 h-5', resolvedUrl ? 'text-white' : 'text-muted-foreground')} />
                  </div>
                )}
                <div className="text-center">
                  <p className={cn('font-semibold text-sm leading-tight', resolvedUrl ? 'text-white' : 'text-foreground')}>
                    {isOwner
                      ? (resolvedUrl ? t('license.replace', 'Replace image') : t('license.upload', 'Upload an image'))
                      : t('license.ownerOnly', 'Only the organization owner can change this')}
                  </p>
                  {isOwner && (
                    <p className={cn('text-[11px] mt-0.5', resolvedUrl ? 'text-white/80' : 'text-muted-foreground')}>
                      {uploading ? t('license.uploading', 'Uploading…') : t('license.clickToUpload', 'Click to upload')}
                    </p>
                  )}
                </div>
              </div>
              {resolvedUrl && !uploading && isOwner && (
                <button
                  className="absolute top-2 end-2 z-10 p-1 rounded-full bg-destructive text-white opacity-0 group-hover:opacity-100 touch-reveal transition-opacity"
                  onClick={e => { e.stopPropagation(); handleRemove() }}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              <input ref={inputRef} type="file" hidden accept="image/*" onChange={handleFile} disabled={!isOwner} />
            </div>
            {!url && (
              <p className="text-xs text-muted-foreground mt-2">
                {t('license.noImageNote', 'Until an image is uploaded, an expired license still drops your screens to the standard pairing code.')}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

